import { DOMParser, DOMSerializer, type ResolvedPos, type Slice } from '@tiptap/pm/model';
import { type EditorView } from '@tiptap/pm/view';

/** Consecutive newlines must become empty paragraphs; ProseMirror's default `+` quantifier drops them. */
const WORKSPACE_AGENT_COMPOSER_CLIPBOARD_LINE_BREAK = /\r?\n/;

type ParseWorkspaceAgentComposerClipboardText = (
  text: string,
  $context: ResolvedPos,
  plain: boolean,
  view: EditorView,
) => Slice;

/**
 * `clipboardTextParser` for the workspace agent composer. Mirrors ProseMirror's default as-text
 * branch (wrap each line in `<p>`, apply `$context.marks()`) so `parseSlice` with context produces
 * an open slice that merges into surrounding text. Closed-block `insertContent` is the seed path.
 */
export const parseWorkspaceAgentComposerClipboardText: ParseWorkspaceAgentComposerClipboardText = (
  text,
  $context,
  _plain,
  view,
) => {
  const { schema } = view.state;
  const serializer = DOMSerializer.fromSchema(schema);
  const marks = $context.marks();
  const dom = document.createElement('div');

  for (const block of text.split(WORKSPACE_AGENT_COMPOSER_CLIPBOARD_LINE_BREAK)) {
    const paragraph = dom.appendChild(document.createElement('p'));

    if (block) {
      paragraph.appendChild(serializer.serializeNode(schema.text(block, marks)));
    }
  }

  const parser = view.someProp('clipboardParser') || view.someProp('domParser') || DOMParser.fromSchema(schema);

  return parser.parseSlice(dom, { context: $context, preserveWhitespace: 'full' });
};
